import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import routePaths from '../../constants/routePaths'
import lottie from 'lottie-web'
import { useContextOnboarding } from '../../contexts/onboardingContext'
import { cn } from '../../utils/cn'
import CloseButton from '../CloseButton'

type Props = {
  isAnimationShowing: boolean
}

const FirstVisitNotification = ({ isAnimationShowing }: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { setIsAnimationShowing } = useContextOnboarding()
  const [fadeOut, setFadeOut] = useState<boolean>(false)
  const lottieContainerRef = useRef<HTMLDivElement>(null)

  const onClose = () => {
    setFadeOut(true)
    setTimeout(() => {
      setFadeOut(false)
      setIsAnimationShowing(false)
    }, 800)
  }

  useEffect(() => {
    if (!isAnimationShowing || !lottieContainerRef.current) return

    const animation = lottie.loadAnimation({
      container: lottieContainerRef.current,
      renderer: 'svg',
      loop: false,
      autoplay: true,
      path: 'animation/motivum_tutorial.json',
    })

    // without destroying it, a re-run of the effect (e.g. StrictMode) appends a second svg
    return () => animation.destroy()
  }, [isAnimationShowing])

  return (
    <div className="absolute left-0 top-0 z-10 size-full bg-black/[0.68]">
      <div
        className={cn(
          'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 cursor-pointer max-sm:h-[300px] max-sm:w-[525px]',
          { 'animate-[simple-fade-out_0.8s_ease_forwards]': fadeOut },
        )}
        onClick={() => {
          setIsAnimationShowing(false)
          navigate(routePaths.GENERATE)
        }}
      >
        {/* lottie gets its own container, because destroy() empties it along with any react children */}
        <div ref={lottieContainerRef} className="size-full" />
        <span className="absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 animate-[simple-fade-in_3.5s_ease_0.5s_forwards] whitespace-nowrap text-center font-approach-bold text-[14px] leading-[32px] tracking-[0.6px] text-red opacity-0 short-sm:text-[18px] md:text-[16px] lg:text-[22px] xl:text-[24px]">
          <div className="mt-[10px] flex items-center justify-center">
            <span>{t('firstVisitNotification')}</span>
            <img className="ml-[45px]" src="/icons/red-arrow.png" alt="red-arrow-icon" />
          </div>
        </span>
      </div>
      <CloseButton onClick={onClose} className={cn({ 'fade-out': fadeOut })} />
    </div>
  )
}

export default FirstVisitNotification
